import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import * as Yup from "yup";
import { useFormik } from "formik";

import { useFetchRulesQuery, useToast } from "@/hooks";
import { DisplayError } from "@/utils";
import { SheetFilterContainer } from "../../sheets";
import { Button, Label, MultiSelect, Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui";


type FormFilterUsersProps = PropsWithChildren;

interface FilterValues {
  roles: string[];
  account_type: string;
}

export function FormFilterUsers({ children }: FormFilterUsersProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const { data: rolesData, isLoading: isLoadingRules } = useFetchRulesQuery();

  const initialValues: FilterValues = {
    roles: ["all"],
    account_type: "all",
  };

  const {
    handleSubmit,
    getFieldProps,
    errors,
    touched,
    isSubmitting,
    resetForm,
    ...formik
  } = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      roles: Yup.array().optional(),
      account_type: Yup.string().optional(),
    }),

    onSubmit: async (data) => {
      await handleFilterPatients(data);
    },
  });

  async function handleFilterPatients(values: any) {
    try {
      if (values.account_type) queryParams.set("account_type", values.account_type);
      if (values.roles)
        queryParams.set("roles", values.roles.join(","));
      navigate({ search: queryParams.toString() });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  function clearFilter() {
    queryParams.delete("account_type");
    queryParams.delete("roles");

    navigate({ search: queryParams.toString() });
    resetForm();
  }

  return (
    <SheetFilterContainer button={children}>
      <div className="h-full overflow-y-auto pb-5 px-1">
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="space-y-6">
            <div className="space-y-1">
              <Label>{t("LABEL.account_type")}</Label>
              <Select
                {...getFieldProps("account_type")}
                onValueChange={(value) => {
                  formik.setFieldValue("account_type", value);
                }}
                defaultValue={formik.values.account_type}
                key={formik.values.account_type}
              >
                <SelectTrigger
                  isInvalid={touched.account_type && errors.account_type}
                  isValid={touched.account_type && !errors.account_type}
                >
                  <SelectValue placeholder={t("LABEL.select an option")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">
                      {t("LABEL.all")}
                    </SelectItem>
                    <SelectItem value="employ">
                      {t("LABEL.employee")}
                    </SelectItem>
                    <SelectItem value="admin">{t("LABEL.admin")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t("LABEL.role")}</Label>
              <div className="space-y-1">
                <MultiSelect
                  options={
                    [
                      { value: "all", label: t("LABEL.all") },
                      ...(rolesData?.roles?.map((rule) => ({
                        value: rule.id,
                        label: t(rule.name),
                      })) || []
                      )
                    ]
                  }
                  loading={isLoadingRules || !rolesData}
                  isInvalid={touched.roles && (errors.roles as string)}
                  isValid={touched.roles && !errors.roles}
                  value={formik.values.roles}
                  onChange={(value) => {
                    formik.setFieldTouched("roles", true);
                    const selected = value as string[];
                    const wasAllSelected = formik.values.roles.includes("all");

                    if (selected.includes("all") && !wasAllSelected) {
                      formik.setFieldValue("roles", ["all"]);
                      return;
                    }

                    if (wasAllSelected && selected.length > 1) {
                      const cleaned = selected.filter((v) => v !== "all");
                      formik.setFieldValue("roles", cleaned);
                      return;
                    }

                    formik.setFieldValue("roles", selected);
                  }}
                  placeholder={t("LABEL.select role")}
                  emptyMessage="Nenhum cargo encontrado"
                  multiple
                  maxItems={5}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-start gap-2">
            <Button type="submit" disabled={isSubmitting}>
              Filtrar
            </Button>
            <Button variant="secondary" type="button" onClick={clearFilter}>
              Limpar filtro
            </Button>
          </div>
        </form>
      </div>
    </SheetFilterContainer>
  );
}
